import type { TPlaceholderElement } from '@alpod-ai-writer/plate-plugins/media';
import {
    FilePlugin,
    ImagePlugin,
    PlaceholderPlugin,
    PlaceholderProvider,
} from '@alpod-ai-writer/plate-plugins/media/react';
import { cn } from '@udecode/cn';
import {
    PlateElement,
    useEditorPlugin,
    withHOC,
    withRef,
} from '@udecode/plate/react';
import { ImageIcon } from 'lucide-react';
import type { LegacyRef } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFilePicker } from 'use-file-picker';

import { AcceptedImageTypes } from '../../constants';
import { useDocumentEditor } from '../../hooks/useDocumentEditor.hook';
import { useUploadFile } from '../../hooks/useUploadFile.hook';
import { Spinner } from '../common/spinner.component';

export const MediaPlaceholderElement = withHOC(
    PlaceholderProvider,
    withRef<typeof PlateElement>(
        ({ children, className, ...props }, ref) => {
            const docEditor = useDocumentEditor();
            const editor = props.editor;
            const element = props.element as TPlaceholderElement;

            const { api } = useEditorPlugin(PlaceholderPlugin);
            const { isUploading, progress, uploadedFile, uploadFile, uploadingFile } = useUploadFile({
                documentId: docEditor.document.documentId
            });

            const loading = isUploading && uploadingFile;

            const isImage = element.mediaType === ImagePlugin.key;
            const imageRef = useRef<HTMLImageElement>(null);

            const { openFilePicker } = useFilePicker({
                accept: AcceptedImageTypes,
                multiple: true,
                onFilesSelected: (data: any) => {
                    if (!data.plainFiles || data.plainFiles.length === 0) return;
                    const updatedFiles = data.plainFiles;
                    const firstFile = updatedFiles[0];
                    const restFiles = updatedFiles.slice(1);
                    replaceCurrentPlaceholder(firstFile);
                    restFiles.length > 0 && (editor as any).tf.insert.media(restFiles);
                },
            });

            const replaceCurrentPlaceholder = useCallback(
                (file: File) => {
                    void uploadFile(file);
                    api.placeholder.addUploadingFile(element.id as string, file);
                },
                [api.placeholder, element.id, uploadFile]
            );

            useEffect(() => {
                if (!uploadedFile) return;
                const path = editor.api.findPath(element);
                editor.tf.withoutSaving(() => {
                    editor.tf.removeNodes({ at: path });
                    const node = {
                        children: [{ text: '' }],
                        initialHeight: imageRef.current?.height,
                        initialWidth: imageRef.current?.width,
                        isUpload: true,
                        name: element.mediaType === FilePlugin.key ? uploadedFile.name : '',
                        placeholderId: element.id as string,
                        type: element.mediaType!,
                        url: uploadedFile.url,
                    };
                    editor.tf.insertNodes(node, { at: path });

                    // Causes bug because history is empty
                    // updateUploadHistory(editor, node);
                });
                api.placeholder.removeUploadingFile(element.id as string);
            }, [uploadedFile, element.id]);

            // React dev mode will call useEffect twice
            const isReplaced = useRef(false);

            /** Paste and drop */
            useEffect(() => {
                if (isReplaced.current) return;
                isReplaced.current = true;
                const currentFiles = api.placeholder.getUploadingFile(element.id as string);
                if (!currentFiles) return;
                replaceCurrentPlaceholder(currentFiles);
            }, [isReplaced]);

            return (
                <PlateElement ref={ref} className={cn(className, 'my-1')} {...props}>
                    {(!loading || !isImage) && (
                        <div
                            className={cn(
                                'flex cursor-pointer items-center justify-center rounded py-3 px-4 select-none bg-gray-200 hover:bg-blue-200 transition-colors duration-200 mb-4'
                            )}
                            onClick={() => !loading && openFilePicker()}
                            contentEditable={false}
                        >
                            <div className="relative mr-3 flex text-muted-foreground/80 [&_svg]:size-6">
                                <ImageIcon size={20} />
                            </div>
                            <div className="text-sm whitespace-nowrap text-muted-foreground">
                                <div>
                                    {loading ? uploadingFile?.name : 'Nahrajte obrázek'}
                                </div>
                                {loading && !isImage && (
                                    <div className="mt-1 flex items-center gap-1.5">
                                        <div>{formatBytes(uploadingFile?.size ?? 0)}</div>
                                        <div>–</div>
                                        <div className="flex items-center">
                                            <Spinner className="mr-1 size-3.5" />
                                            {progress ?? 0}%
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {isImage && loading && (
                        <ImageProgress
                            file={uploadingFile}
                            imageRef={imageRef}
                            progress={progress}
                        />
                    )}
                    {children}
                </PlateElement>
            );
        }
    )
);
export function ImageProgress({
    className,
    file,
    imageRef,
    progress = 0,
}: {
  file: File;
  className?: string;
  imageRef?: React.RefObject<HTMLImageElement | null>;
  progress?: number;
}) {
    const [objectUrl, setObjectUrl] = useState<string | null>(null);
    useEffect(() => {
        const url = URL.createObjectURL(file);
        setObjectUrl(url);
        return () => {
            URL.revokeObjectURL(url);
        };
    }, [file]);
    if (!objectUrl) {
        return null;
    }
    return (
        <div className={cn('relative', className)} contentEditable={false}>
            <img
                ref={imageRef as LegacyRef<HTMLImageElement>}
                className="h-auto w-full rounded-sm object-cover"
                alt={file.name}
                src={objectUrl}
            />
            {progress < 100 && (
                <div className="absolute right-1 bottom-1 flex items-center space-x-2 rounded-full bg-black/50 px-1 py-0.5">
                    <Spinner />
                    <span className="text-xs font-medium text-white">
                        {Math.round(progress)}%
                    </span>
                </div>
            )}
        </div>
    );
}
export function formatBytes(
    bytes: number,
    opts: {
    decimals?: number;
    sizeType?: 'accurate' | 'normal';
  } = {}
) {
    const { decimals = 0, sizeType = 'normal' } = opts;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const accurateSizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB'];
    if (bytes === 0) return '0 Byte';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${
        sizeType === 'accurate'
            ? (accurateSizes[i] ?? 'Bytest')
            : (sizes[i] ?? 'Bytes')
    }`;
}
